import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  treatmentPackagesApi,
  treatmentPackagesQueryKeys,
} from "../../../shared/api/treatmentPackages";
import { apiError } from "../../../shared/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const money = (n: number) => n.toLocaleString("vi-VN") + " ₫";
const statusLabel: Record<string, string> = {
  ACTIVE: "Đang dùng",
  COMPLETED: "Hoàn tất",
  EXPIRED: "Hết hạn",
  CANCELLED: "Đã huỷ",
};
const statusVariant: Record<string, BadgeProps["variant"]> = {
  ACTIVE: "success",
  COMPLETED: "secondary",
  EXPIRED: "warning",
  CANCELLED: "destructive",
};

export function PatientPackagesSection({ patientId }: { patientId: string | undefined }) {
  const qc = useQueryClient();
  const pid = patientId ?? "";
  const pkgs = useQuery({
    queryKey: treatmentPackagesQueryKeys.ofPatient(pid),
    queryFn: () => treatmentPackagesApi.forPatient(pid),
    enabled: !!patientId,
  });
  const catalog = useQuery({
    queryKey: treatmentPackagesQueryKeys.catalog(),
    queryFn: () => treatmentPackagesApi.catalog(true),
  });

  const [pkgToBuy, setPkgToBuy] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: treatmentPackagesQueryKeys.ofPatient(pid) });
  };

  const buy = useMutation({
    mutationFn: () => treatmentPackagesApi.buy(pid, pkgToBuy),
    onSuccess: () => {
      invalidate();
      setMsg("Đã bán gói cho khách.");
      setError(null);
      setPkgToBuy("");
    },
    onError: (e: unknown) => {
      setError(apiError(e).detail);
      setMsg(null);
    },
  });

  const redeem = useMutation({
    mutationFn: (id: string) => treatmentPackagesApi.redeem(id),
    onSuccess: () => {
      invalidate();
      setMsg("Đã trừ 1 buổi.");
      setError(null);
    },
    onError: (e: unknown) => {
      setError(apiError(e).detail);
      setMsg(null);
    },
  });

  if (!patientId) return null;
  const list = pkgs.data ?? [];

  return (
    <div className="grid gap-4">
      <Card>
        <CardContent className="p-4">
          <h3 className="mb-2 text-[15px] font-semibold text-foreground">Gói của khách</h3>
          {pkgs.isLoading ? (
            <p className="text-sm text-muted-foreground">Đang tải…</p>
          ) : list.length === 0 ? (
            <p className="text-sm text-muted-foreground">Khách chưa mua gói nào.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Gói</TableHead>
                  <TableHead className="text-center">Còn lại</TableHead>
                  <TableHead>HSD</TableHead>
                  <TableHead>Trạng thái</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.packageName}</TableCell>
                    <TableCell className="text-center">
                      <strong>{p.sessionsRemaining}</strong>/{p.sessionsTotal}
                    </TableCell>
                    <TableCell>{p.expiresAt ?? "—"}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant[p.status] ?? "muted"}>
                        {statusLabel[p.status] ?? p.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {p.status === "ACTIVE" && p.sessionsRemaining > 0 && (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={redeem.isPending}
                          onClick={() => {
                            setMsg(null);
                            setError(null);
                            redeem.mutate(p.id);
                          }}
                        >
                          Dùng 1 buổi
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="grid gap-2.5 p-4">
          <h3 className="text-[15px] font-semibold text-foreground">Bán gói cho khách</h3>
          <div className="flex flex-wrap items-center gap-3">
            <Select value={pkgToBuy} onValueChange={setPkgToBuy}>
              <SelectTrigger className="w-full min-w-[260px] sm:w-auto">
                <SelectValue placeholder="— Chọn gói —" />
              </SelectTrigger>
              <SelectContent>
                {(catalog.data ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} · {c.totalSessions} buổi · {money(c.priceVnd)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              disabled={!pkgToBuy || buy.isPending}
              onClick={() => {
                setMsg(null);
                setError(null);
                buy.mutate();
              }}
            >
              {buy.isPending ? "Đang bán…" : "Bán gói"}
            </Button>
          </div>
          {(catalog.data ?? []).length === 0 && (
            <small className="text-muted-foreground">
              Chưa có gói nào đang bán. Tạo ở Cài đặt → Gói liệu trình.
            </small>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          {msg && <p className="text-sm text-emerald-600">{msg}</p>}
        </CardContent>
      </Card>
    </div>
  );
}
